/*
 *  author: allen
 *  音频播放器
 * @class AudioPlayer
 * e.g AudioPlayer.init({...}) or new AudioPlayer()
 * */
class AudioPlayer {
  static audioPlayer = null;

  /*
   * @param {string} src 资源
   * @param {boolean} [autoplay = false] 是否自动播放
   * @param {boolean} [muted = false] 是否静音
   * @param {boolean} [loop = false] 循环播放
   * @param {number} [loopCount = 1] 播放次数
   * @param {function} [playCallback = function() {}] 播放成功回调
   * @param {function} [pauseCallback = function() {}] 暂停成功回调
   * @param {function} [endCallback = function() {}] 播放结束回调
   * */
  constructor({
    src,
    autoplay = false,
    muted = false,
    loop = false,
    loopCount = 1,
    playCallback = function () {},
    pauseCallback = function () {},
    endCallback = function () {}
  }) {
    this.audio = new Audio();
    this.audio.src = src;
    this.audio.muted = muted;

    this.__canPlay__ = false;
    this.__count__ = 0;
    this.__stopFlag__ = false;

    this.hasPending = false;

    this.status = 'init';

    this.options = new Proxy(
      {
        src,
        autoplay,
        loop,
        loopCount,
        playCallback,
        pauseCallback,
        endCallback
      },
      {
        get: (target, property) => {
          return target[property];
        },
        set: (target, property, value) => {
          if (property === 'src') {
            /*
             * 改变资源时，初始化状态
             * */
            this.status = 'init';
            this.audio.src = value;
            this.__canPlay__ = false;
            this.audio.load();
          }

          if (property === 'loopCount') {
            if (target.loop) {
              target.loop = false;
            }
          }

          return Reflect.set(target, property, value);
        }
      }
    );

    this.audio.addEventListener('play', () => {
      this.status = 'play';
      this.options.playCallback();
    });

    this.audio.addEventListener('canplaythrough', () => {
      this.__canPlay__ = true;
      if (this.status !== 'play') {
        if (this.options.autoplay) {
          this.play();
        }

        if (this.hasPending) {
          this.hasPending = false;
          this.play();
        }
      }
    });

    this.audio.addEventListener('pause', () => {
      this.status = 'pause';
      this.options.pauseCallback();
    });

    this.audio.addEventListener('ended', () => {
      if ((this.__count__ < this.options.loopCount || this.options.loop) && !this.__stopFlag__) {
        this.play();
      } else {
        this.status = 'init';
        this.__count__ = 0;
        this.__stopFlag__ = false;
        this.options.endCallback();
      }
    });

    return new Proxy(this, {
      get(target, property) {
        return target[property];
      },
      set(target, property, value) {
        if (typeof target[property] === 'function' || target[property] instanceof HTMLAudioElement) {
          return true;
        }
        return Reflect.set(target, property, value);
      }
    });
  }

  /*
   * 单例调用
   * @param {string} src 资源
   * @param {boolean} [autoplay = false] 是否自动播放
   * @param {boolean} [muted = false] 是否静音
   * @param {boolean} [loop = false] 循环播放
   * @param {number} [loopCount = 1] 播放次数
   * @param {function} [playCallback = function() {}] 播放成功回调
   * @param {function} [pauseCallback = function() {}] 暂停成功回调
   * @param {function} [endCallback = function() {}] 播放结束回调
   * */
  static init({ src, autoplay, muted, loop, loopCount, playCallback, pauseCallback, endCallback }) {
    if (!AudioPlayer.audioPlayer) {
      AudioPlayer.audioPlayer = new AudioPlayer({
        src,
        autoplay,
        muted,
        loop,
        loopCount,
        playCallback,
        pauseCallback,
        endCallback
      });
    }
    return AudioPlayer.audioPlayer;
  }

  /*
   * 修改属性
   * */
  changeOptions({ src, muted, loop, loopCount, playCallback, pauseCallback, endCallback }) {
    if (this.audio) {
      if (src) {
        this.audio.src = src;
      }

      if (muted !== undefined) {
        this.audio.muted = muted;
      }

      if (loop !== undefined) {
        this.audio.loop = muted;
      }

      if (loopCount) {
        this.options.loopCount = loopCount;
      }

      if (playCallback) {
        this.options.playCallback = playCallback;
      }

      if (pauseCallback) {
        this.options.pauseCallback = pauseCallback;
      }

      if (endCallback) {
        this.options.endCallback = endCallback;
      }

      this.__canPlay__ = false;
      this.audio.load();
    }
  }

  /*
   * 单例释放资源
   * */
  dispose() {
    AudioPlayer.audioPlayer = null;
  }

  /*
   * 播放音频
   * */
  play() {
    if (this.audio && this.__canPlay__ && this.status !== 'play') {
      this.__stopFlag__ = false;
      this.__count__ = this.__count__ + 1;
      this.audio.play().then(() => {});
    } else {
      /*
       * 当音频还未加载完时先暂存一下
       * */
      this.hasPending = true;
    }
  }

  /*
   * 暂停音频
   * @description 音频播放中停止
   * */
  pause() {
    if (this.audio) {
      this.audio.pause();
    }
  }

  /*
   * 停止音频
   * @description 音频播放完后停止
   * */
  stop() {
    if (this.audio) {
      this.__stopFlag__ = true;
      this.__count__ = 0;
    }
  }
}

export default AudioPlayer;
